import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import posthog from 'posthog-js';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { UiMode } from 'src/assets/data/enum/uiMode';
import {
  EventTracker,
  EventTrackerAvailableSection,
  EventTrackerClickMapper,
  EventTrackerModulesMapper,
} from '../../../assets/data/enum/eventTracker';
import { GeneralUtilsService } from './common/generalUtils.service';

type Mode = 'agro' | 'livestock';

const SECTION_SESSION_START = 'section_session_start';
const SECTION_SESSION_END = 'section_session_end';

interface PostHogEventProperties {
  mode: Mode;
  section: EventTrackerAvailableSection;
  start_timestamp?: string;
  end_timestamp?: string;
  duration_seconds?: number;
}

@Injectable({
  providedIn: 'root',
})
export class EventTrackerService implements OnDestroy {
  private router = inject<Router>(Router);

  private subscribers: Subscription = new Subscription();

  private generalUtilsService = inject(GeneralUtilsService);

  private modeUI = toSignal(this.generalUtilsService.globalUIMode);
  private eventMode = computed(() => (this.modeUI() === UiMode.FARM ? 'agro' : 'livestock'));

  eventTrackingActivated = signal(false);
  currentModule = signal<PostHogEventProperties>(null);

  constructor() {
    if (!this.isEnabled()) {
      return;
    }

    // This event is observed here to ensure that all modules are tracked only once and exclusively when they are activated.
    const routerSub = combineLatest([
      this.generalUtilsService.globalUIMode,
      this.router.events.pipe(
        filter((event) => event instanceof NavigationStart)
      ) as BehaviorSubject<NavigationStart>,
    ]).subscribe(([_, routerEvent]) => {
      if (!this.isEnabled()) {
        return;
      }

      this.trackModuleEvent(routerEvent.url);
    });

    this.subscribers.add(routerSub);
  }

  /**
   * Description - Initialises the connection to the PostHog event tracked service, reading from the environment the postHogKey for the PostHog connection.
   */
  init(): void {
    try {
      posthog.init(environment.postHogKey, {
        api_host: environment.eventTrackingEndpoint || 'https://app.posthog.com',
        ui_host: 'https://eu.i.posthog.com',
        autocapture: true,
        capture_pageview: true,
      });
      console.log('[PostHog] client ready');
    } catch (error) {
      console.error('[PostHog] init error:', error);
    }
  }

  trackModuleEvent(url: string) {
    const moduleUrl = this.getModuleUrl(url);

    // if somehow module url doesn't exist
    if (!moduleUrl) {
      return;
    }

    const section = EventTrackerModulesMapper[this.modeUI()][moduleUrl];

    // if module/section is not tracked
    if (!section) {
      this.endLastSessionIfExist(new Date().toString());
      this.currentModule.set(null);
      return;
    }

    const mode = this.eventMode();
    // if the user is already in the same module, do nothing
    if (this.currentModule()?.section === section && this.currentModule()?.mode === mode) {
      return;
    }

    const start_timestamp = new Date().toString();
    this.endLastSessionIfExist(start_timestamp);
    this.trackEvent(SECTION_SESSION_START, { mode, section, start_timestamp });

    this.currentModule.set({ mode, section, start_timestamp });
  }

  /**
   * Track on PostHog a custom event.
   * @param eventName - Name of the custom event to track.
   * @param properties - Additional Properties of the event to be tracked. If not provided we assume it's a click event and we get the properties from the EventTrackerClickMapper.
   */
  trackEvent(eventName: string, properties?: PostHogEventProperties) {
    if (!eventName) return;
    const { mode, section, start_timestamp } =
      properties ?? this.getClickEventProperties(eventName);

    if (this.isEnabled() && this.eventTrackingActivated()) {
      posthog.capture(eventName, { mode, section, start_timestamp, ...properties });
    }
  }

  /**
   * Description - Identifies the user to be tracked on PostHog.
   * @param userId - Id of the logged User.
   * @param properties - Additional properties of the User to be track.
   */
  identifyUser(userId: string, properties?): void {
    if (!this.isEnabled()) {
      return;
    }

    console.log('[PostHog] identify user');
    this.eventTrackingActivated.set(true);
    posthog.identify(userId, properties);
    posthog.opt_in_capturing();
    posthog.config.autocapture = true;
  }

  /**
   * Description - Reset the PostHog connection.
   */
  reset(): void {
    if (!this.isEnabled()) {
      return;
    }

    console.log('[PostHog] client reset');
    this.eventTrackingActivated.set(false);
    posthog.config.autocapture = false;
    posthog.opt_out_capturing();
    posthog.reset();
  }

  /**
   * Description - Subscribe to tab closed event to notify it.
   */
  registerTabCloseEvent(): void {
    if (!this.isEnabled()) {
      return;
    }

    const onPageHide = (): void => {
      const payload = {
        event: EventTracker.CLOSE_APPLICATION_WEB,
        properties: {},
        distinct_id: posthog.get_distinct_id(),
        timestamp: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

      navigator.sendBeacon(`${environment.eventTrackingEndpoint}/e/`, blob);
    };

    window.addEventListener('pagehide', onPageHide);
  }

  isEnabled() {
    const isLocalhost = window.location.hostname.includes('localhost');
    return environment.eventTrackingEnabled && !isLocalhost;
  }

  private getClickEventProperties(event: string) {
    const mode = this.eventMode();
    const section = EventTrackerClickMapper[event];
    const start_timestamp = new Date().toString();
    return { mode, section, start_timestamp };
  }

  private getModuleUrl(url: string) {
    const afterAuth = url.split('auth/')[1];
    if (!afterAuth) {
      return '';
    }
    // special cases
    if (
      afterAuth.split('/')[0] === 'company-data' ||
      afterAuth === 'satellite/manage-prescription' ||
      afterAuth === 'satellite/manage-files'
    ) {
      return afterAuth;
    }

    return afterAuth.split('/')[0];
  }

  ngOnDestroy() {
    this.subscribers.unsubscribe();
  }

  private endLastSessionIfExist(timestamp: string) {
    if (!this.currentModule()) {
      return;
    }
    const date = new Date(timestamp);
    const startDate = new Date(this.currentModule().start_timestamp);
    const durationSeconds = Math.round((date.getTime() - startDate.getTime()) / 1000) || 0;

    const endEventProperties: PostHogEventProperties = {
      ...this.currentModule(),
      end_timestamp: timestamp,
      duration_seconds: durationSeconds,
    };

    this.trackEvent(SECTION_SESSION_END, endEventProperties);
  }
}
